import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { Text } from 'admin-components/components';

import { predictDate } from './DatePicker.utils';
import { Input, Label, InputContainer, DatepickerContainer, StyledCalendar } from './DatePicker.styles';

const expectedDateFormat = 'dddd DD MMMM YYYY';

const DatePicker = ({
  intl: { formatMessage, locale },
  id,
  name,
  label,
  value,
  setTileClassName,
  minDate,
  maxDate,
  className,
  multicity,
  errorMessage,
  setErrorMessage,
  activeStartDate,
  setTravelDate,
  clearTravelDate
}) => {
  const inputRef = useRef();

  const handleTabKey = () => {
    const buttons = [...document.querySelectorAll('.react-calendar button')];
    buttons.forEach((button) => button.setAttribute('tabIndex', '-1'));
  };

  useEffect(() => {
    handleTabKey();
  }, []);

  useEffect(() => {
    if (inputRef.current && value === null) {
      inputRef.current.value = '';
    }

    if (value) {
      const formattedDate = moment(value).format(expectedDateFormat);

      if (inputRef.current.value !== formattedDate) {
        inputRef.current.value = formattedDate;
      }
    }
  }, [value]);

  const handleTextInputFocus = () => {
    handleTabKey();

    if (errorMessage?.length) {
      setErrorMessage?.('');
    }
  };

  const handleTextInputBlur = (event) => {
    const { value } = event.target;

    const dateObj = predictDate(value);

    if (dateObj.isValid()) {
      setTravelDate(id, dateObj);

      const formattedDate = dateObj.format(expectedDateFormat);

      inputRef.current.value = formattedDate;
    } else if (value) {
      clearTravelDate(id);
      setErrorMessage('invalidDateMessage');
    } else {
      clearTravelDate(id);
    }
  };

  const handleTextInputChange = (event) => {
    const { value } = event.target;

    if (!value) {
      clearTravelDate?.(id, '');
    }
  };

  const handleCalendarChange = (date) => {
    const dateObj = moment(date);
    setTravelDate(id, dateObj);

    const formattedDate = dateObj.format(expectedDateFormat);

    inputRef.current.value = formattedDate;
  };

  const calendarMinDate = minDate && minDate.isValid() ? minDate.toDate() : null;
  const calendarMaxDate = maxDate && maxDate.isValid() ? maxDate.toDate() : null;
  const inputDefaultValue = value && value.isValid() ? value.format(expectedDateFormat) : '';
  const activeStartDateValue = activeStartDate && activeStartDate.isValid() ? activeStartDate.toDate() : new Date();
  const calendarValue = value && value.isValid() ? value.toDate() : null;

  return (
    <DatepickerContainer className={className} multicity={multicity}>
      <InputContainer>
        <Label htmlFor={`${id}-input`}>{label}</Label>
        <Input
          ref={inputRef}
          onFocus={handleTextInputFocus}
          data-testid={`${id}-input`}
          id={`${id}-input`}
          defaultValue={inputDefaultValue}
          name={name}
          onBlur={handleTextInputBlur}
          onChange={handleTextInputChange}
        />
      </InputContainer>
      {errorMessage && (
        <Text id={`${id}-error`} color="red">
          {formatMessage({ id: `bookingMagnet.${errorMessage}` })}
        </Text>
      )}
      <StyledCalendar
        activeStartDate={activeStartDateValue}
        showNeighboringMonth={false}
        tabIndex="-1"
        value={calendarValue}
        onChange={handleCalendarChange}
        minDate={calendarMinDate}
        maxDate={calendarMaxDate}
        id={`${id}-calendar`}
        tileClassName={setTileClassName}
        onActiveDateChange={handleTabKey}
        locale={locale}
      />
    </DatepickerContainer>
  );
};

DatePicker.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
    locale: PropTypes.string
  }),
  name: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.node,
  value: PropTypes.instanceOf(moment),
  setTileClassName: PropTypes.func,
  minDate: PropTypes.instanceOf(moment),
  maxDate: PropTypes.instanceOf(moment),
  className: PropTypes.string,
  multicity: PropTypes.bool,
  setTravelDate: PropTypes.func,
  clearTravelDate: PropTypes.func,
  errorMessage: PropTypes.string,
  setErrorMessage: PropTypes.func,
  activeStartDate: PropTypes.instanceOf(moment)
};

export default DatePicker;
